import { LogIn } from "lucide-react";
import { useEffect, useState } from "react";
import { AccountsView } from "./AccountsView";
import { api, ApiError, type Session } from "./api";
import { Wordmark } from "./brand";
import { CacheView } from "./CacheView";
import { demoSession } from "./demo";
import { InvestorsView } from "./InvestorsView";
import { InvitationsView } from "./InvitationsView";
import { Shell } from "./Shell";
import { TelemetryView } from "./TelemetryView";
import { StacksView } from "./StacksView";
import { ReviewView } from "./ReviewView";
import { StaffView } from "./StaffView";
import type { ModuleId } from "./types";
import { Notice, useDensity } from "./ui";

const demo = import.meta.env.DEV && new URLSearchParams(location.search).has("demo");

const modulePermission: Record<ModuleId, string> = {
  accounts: "accounts.read",
  invitations: "invitations.manage",
  telemetry: "telemetry.read",
  investors: "investors.read",
  cache: "cache.read",
  stacks: "stacks.read",
  review: "stacks.read",
  staff: "staff.manage"
};

/** A link may open a module directly: /?module=stacks&q=... (the Review item view's Stacks link). */
const linked = new URLSearchParams(location.search).get("module");
const initialModule: ModuleId =
  linked && linked in modulePermission ? (linked as ModuleId) : "accounts";

export function App() {
  const [session, setSession] = useState<Session>();
  const [active, setActive] = useState<ModuleId>(initialModule);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [density, setDensity] = useDensity();

  useEffect(() => {
    if (demo) {
      setSession(demoSession);
      setLoading(false);
      return;
    }
    void api<Session>("/api/session")
      .then(setSession)
      .catch((cause: unknown) => {
        if (!(cause instanceof ApiError && cause.status === 401))
          setError(cause instanceof Error ? cause.message : "The admin console could not start");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!session || session.permissions.includes(modulePermission[active])) return;
    const first = (Object.keys(modulePermission) as ModuleId[]).find((module) =>
      session.permissions.includes(modulePermission[module])
    );
    if (first) setActive(first);
  }, [session, active]);

  if (loading)
    return (
      <main className="entry">
        <div className="entry-card bevel">
          <Wordmark />
          <h1>Opening your staff session</h1>
          <p role="status">Confirming your Evidite account and permissions.</p>
        </div>
      </main>
    );

  if (!session)
    return (
      <main className="entry">
        <div className="entry-card bevel">
          <Wordmark />
          <h1>Admin console</h1>
          <p>
            One console for accounts and credits, product telemetry, the investor pipeline, and the
            CitateGenie v5 cache.
          </p>
          {error && <Notice tone="error">{error}</Notice>}
          <a className="btn btn-primary" href="/auth/login">
            <LogIn aria-hidden="true" /> Sign in with Evidite
          </a>
          <small>
            Staff access requires an assigned role and multi-factor authentication. Each module is
            granted separately.
          </small>
        </div>
      </main>
    );

  async function signOut() {
    if (!demo) await api("/api/logout", session, { method: "POST" });
    location.assign("/");
  }

  const props = { session, demo, density, onDensityChange: setDensity };

  return (
    <Shell
      session={session}
      active={active}
      onChange={setActive}
      onLogout={() => void signOut()}
      demo={demo}
    >
      {active === "accounts" && session.permissions.includes("accounts.read") && (
        <AccountsView {...props} />
      )}
      {active === "invitations" && <InvitationsView session={session} demo={demo} />}
      {active === "telemetry" && <TelemetryView session={session} demo={demo} />}
      {active === "investors" && <InvestorsView {...props} />}
      {active === "cache" && <CacheView {...props} />}
      {active === "stacks" && <StacksView session={session} demo={demo} />}
      {active === "review" && session.permissions.includes("stacks.read") && (
        <ReviewView session={session} />
      )}
      {active === "staff" && session.permissions.includes("staff.manage") && (
        <StaffView session={session} demo={demo} />
      )}
    </Shell>
  );
}
